import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReferralCodeInput from "@/components/ReferralCodeInput";
import PromoCodeInput from "@/components/PromoCodeInput";
import { AmericaPromoPricingBanner, AmericaPromoCheckoutConfirm } from "@/lib/america250";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { canShowWebCheckout } from "@/lib/platform";
import { Check, Sparkles, ArrowRight, Trophy, Zap, Flame, Rocket, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { capture } from "@/lib/analytics";

const MONTHLY_PRICE = 9.99;
const ANNUAL_PRICE_TOTAL = 99;
const ANNUAL_SAVINGS_PCT = 17; // ($9.99 × 12 = $119.88) → $99 saves ~$20.88 (~17%)
const MONTHLY_CREDITS = 150;

const FREE_FEATURES = [
  "1 personalized challenge / day",
  "Belt progression — all 8 belts",
  "Streaks and leaderboards",
  "Up to 3 AI identity paths",
  "Referral rewards",
];

const MASTERY_FEATURES = [
  { text: "Unlimited daily challenges", emphasized: true },
  { text: "Personal AI Coaches for your goals", emphasized: true },
  { text: "Full challenge archive — every belt path" },
  { text: "Advanced specialized tracks" },
  { text: "1.5× faster belt progression" },
  { text: `${MONTHLY_CREDITS} AI Power credits / month` },
  { text: "Priority challenge recommendations" },
  { text: "Advanced progress analytics" },
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const firstWin = searchParams.get("first_win") === "1";
  const [loadingPlan, setLoadingPlan] = useState(null); // 'mastery_monthly' | 'mastery_annual' | null
  const [error, setError] = useState(null);
  const [promo, setPromo] = useState(""); // applied promo code (string)
  const [promoInfo, setPromoInfo] = useState(null); // validated promo metadata
  const navigate = useNavigate();
  // Phase 1 of iOS migration — hide Stripe purchase entry points on native
  // platforms to comply with Apple Guideline 3.1.1. Phase 2 will replace this
  // with RevenueCat native purchase buttons.
  const webCheckoutAllowed = canShowWebCheckout();

  const currentTier = user?.tier === "mastery" || user?.tier === "pro" ? 2 : 0;
  const isSubscriber = !!user?.has_subscription;

  useEffect(() => {
    capture("pricing_viewed", { first_win: firstWin });
  }, [firstWin]);

  const startCheckout = async (plan) => {
    if (!user) {
      navigate("/login?returnTo=/pricing");
      return;
    }
    setError(null);
    setLoadingPlan(plan);
    try {
      const interval = plan === "mastery_annual" ? "annual" : "monthly";
      // Tag referral redemption attempts so PostHog can attribute revenue
      const referralEligible = interval === "monthly"
        ? !!user?.referral_monthly_available
        : !!user?.referral_annual_available;
      // Guard: if a promo is applied but it doesn't cover the chosen plan, warn early.
      if (promo && promoInfo) {
        const applies = promoInfo.applies_to_plans || [];
        if (applies.length > 0 && !applies.includes(plan)) {
          setLoadingPlan(null);
          setError(`Promo ${promo} doesn't apply to the ${interval === "annual" ? "Annual" : "Monthly"} plan. Switch plans or remove the code.`);
          capture("promo_validation_failed", {
            code_prefix: promo.slice(0, 6),
            reason: "not_applicable",
            attempted_plan: plan,
          });
          return;
        }
      }
      capture("mastery_purchase_started", { interval, promo: promo || null, referral_eligible: referralEligible });
      if (referralEligible) {
        capture(interval === "monthly" ? "referral_monthly_discount_redeemed" : "referral_annual_discount_redeemed", { interval });
      }
      if (promo && promoInfo) {
        // Treat checkout-start as the redemption intent — PostHog can fold this
        // into a conversion funnel against the eventual purchase_succeeded event.
        capture("promo_redeemed", {
          code_prefix: promo.slice(0, 6),
          discount_type: promoInfo.discount_type,
          discount_value: promoInfo.discount_value,
          plan,
          saved: promoInfo.saved,
        });
      }
      const r = await apiClient.post("/billing/checkout", {
        plan,
        origin_url: window.location.origin,
        promo_code: promo || undefined,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Could not start checkout. Please try again.");
      setLoadingPlan(null);
    }
  };

  const headline = firstWin
    ? <>Keep the <em>momentum</em> going.</>
    : <>Continue Your <em>AI Mastery</em> Journey.</>;

  const subline = firstWin
    ? "You felt how it works. Now unlock unlimited challenges, advanced tracks, and 150 monthly AI Power credits — and belt faster."
    : "Unlock unlimited challenges, advanced learning paths, and 150 monthly AI Power credits. Advance faster than 96% of users.";

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 pb-28 md:pb-20">
        <AmericaPromoPricingBanner />
        {firstWin && (
          <div
            className="brut p-5 mb-8 flex items-start gap-3"
            style={{
              background: "linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(16,185,129,0.08) 100%)",
              borderColor: "rgba(245,158,11,0.35)",
            }}
            data-testid="pricing-first-win-banner"
          >
            <Trophy size={22} className="mt-0.5 text-[#F59E0B] flex-shrink-0" />
            <div>
              <div className="font-display text-lg leading-tight text-[var(--ink)]">
                First challenge cleared — belt progress earned.
              </div>
              <div className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed">
                You're already ahead of users who never start. Now unlock the rest of your path.
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-12 max-w-2xl mx-auto">
          <span className="kicker">{firstWin ? "Unlock your full path" : "AI Mastery Journey"}</span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl mt-3 leading-[1.02] tracking-tight">
            {headline}
          </h1>
          <p className="mt-5 text-[var(--ink-2)] text-base sm:text-lg leading-relaxed">{subline}</p>
        </div>

        {/* Plan cards — Free / Monthly / Annual */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 max-w-6xl mx-auto">
          {/* Starting Your Journey (Free) */}
          <div
            className="brut bg-white p-6 md:p-7 relative flex flex-col"
            data-testid="pricing-starting-card"
          >
            <div className="flex items-center gap-2.5 mb-1">
              <span className="kicker">Starting Your Journey</span>
            </div>
            <h2 className="font-display text-2xl md:text-[26px] mt-1 leading-tight">Free forever</h2>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="font-display text-5xl">$0</span>
              <span className="text-[var(--ink-3)] text-sm">/ always</span>
            </div>
            <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
              Build the habit. One challenge a day, every day.
            </p>

            <ul className="mt-6 space-y-2.5 text-sm flex-1">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[var(--ink)]">
                  <Check size={15} className="mt-0.5 text-[var(--emerald,#10B981)] flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {currentTier === 0 ? (
              <div className="mt-6 pt-5 border-t border-[var(--line)] text-center text-xs text-[var(--ink-3)]" data-testid="pricing-starting-current">
                You're here · start a challenge from your dashboard
              </div>
            ) : (
              <Link to="/dashboard" className="btn-ghost mt-6 justify-center">
                Back to dashboard
              </Link>
            )}
          </div>

          {/* AI Mastery — Monthly */}
          <div
            className="brut bg-white p-6 md:p-7 relative flex flex-col"
            data-testid="pricing-mastery-monthly-card"
          >
            <div className="flex items-center gap-2.5 mb-1">
              <Rocket size={16} className="text-[var(--primary,#2563EB)]" />
              <span className="kicker">AI Mastery · Monthly</span>
            </div>
            <h2 className="font-display text-2xl md:text-[26px] mt-1 leading-tight">Advance faster</h2>

            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="font-display text-5xl">${MONTHLY_PRICE}</span>
              <span className="text-[var(--ink-3)] text-sm">/ month</span>
            </div>
            <p className="text-xs text-[var(--ink-3)] mt-1">
              Cancel anytime.
            </p>

            <ul className="mt-6 space-y-2.5 text-sm flex-1">
              {MASTERY_FEATURES.map((f) => (
                <li key={f.text} className="flex items-start gap-2 text-[var(--ink)]">
                  <Check size={15} className="mt-0.5 text-[var(--primary,#2563EB)] flex-shrink-0" />
                  <span className={f.emphasized ? "font-semibold" : ""}>{f.text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-5 border-t border-[var(--line)] space-y-3">
              {webCheckoutAllowed && !isSubscriber && (
                <PromoCodeInput
                  applied={promo && promoInfo ? { code: promo, info: promoInfo } : null}
                  onApply={(code, info) => {
                    setPromo(code);
                    setPromoInfo(info);
                    setError(null);
                    toast.success(`Promo ${code} applied at checkout.`);
                  }}
                  onClear={() => {
                    setPromo("");
                    setPromoInfo(null);
                  }}
                />
              )}
              <AmericaPromoCheckoutConfirm code={promo} applied={!!(promo && promoInfo)} />
              {isSubscriber ? (
                <div className="text-center text-xs text-[var(--ink-3)]" data-testid="pricing-mastery-monthly-current">
                  You're already on Mastery — manage your plan from Settings.
                </div>
              ) : !webCheckoutAllowed ? (
                <NativeWebCheckoutNotice testid="pricing-mastery-monthly-native-notice" />
              ) : (
                <button
                  type="button"
                  onClick={() => startCheckout("mastery_monthly")}
                  disabled={loadingPlan !== null}
                  className="btn-primary w-full justify-center"
                  data-testid="pricing-mastery-monthly-cta"
                  data-track="checkout-click"
                >
                  {loadingPlan === "mastery_monthly" ? <span className="spinner" /> : <>Choose Monthly <ArrowRight size={14} /></>}
                </button>
              )}
            </div>
          </div>

          {/* AI Mastery — Annual (Recommended) */}
          <div
            className="brut-lg p-6 md:p-7 relative flex flex-col"
            style={{
              background: "linear-gradient(135deg, #FFFFFF 0%, #F5F7FF 100%)",
            }}
            data-testid="pricing-mastery-annual-card"
          >
            <div
              className="absolute -top-3 right-6 pill"
              style={{
                background: "var(--ink, #0F1419)",
                color: "#fff",
                borderColor: "var(--ink, #0F1419)",
                fontSize: "0.7rem",
                padding: "0.35rem 0.85rem",
              }}
            >
              ⚡ Best value
            </div>
            <div className="flex items-center gap-2.5 mb-1">
              <Rocket size={16} className="text-[var(--primary,#2563EB)]" />
              <span className="kicker">AI Mastery · Annual</span>
            </div>
            <h2 className="font-display text-2xl md:text-[26px] mt-1 leading-tight">Commit to mastery</h2>

            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="font-display text-5xl">${ANNUAL_PRICE_TOTAL}</span>
              <span className="text-[var(--ink-3)] text-sm">/ year</span>
              <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[var(--emerald,#10B981)] text-white">
                −{ANNUAL_SAVINGS_PCT}%
              </span>
            </div>
            <p className="text-xs text-[var(--ink-3)] mt-1">
              Billed yearly · save ${(MONTHLY_PRICE * 12 - ANNUAL_PRICE_TOTAL).toFixed(2)} vs monthly.
            </p>

            <ul className="mt-6 space-y-2.5 text-sm flex-1">
              {MASTERY_FEATURES.map((f) => (
                <li key={f.text} className="flex items-start gap-2 text-[var(--ink)]">
                  <Check size={15} className="mt-0.5 text-[var(--primary,#2563EB)] flex-shrink-0" />
                  <span className={f.emphasized ? "font-semibold" : ""}>{f.text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-5 border-t border-[var(--line)] space-y-3">
              {webCheckoutAllowed && <ReferralCodeInput centered={false} />}

              {webCheckoutAllowed && !isSubscriber && (
                <PromoCodeInput
                  applied={promo && promoInfo ? { code: promo, info: promoInfo } : null}
                  onApply={(code, info) => {
                    setPromo(code);
                    setPromoInfo(info);
                    setError(null);
                    toast.success(`Promo ${code} applied at checkout.`);
                  }}
                  onClear={() => {
                    setPromo("");
                    setPromoInfo(null);
                  }}
                />
              )}
              <AmericaPromoCheckoutConfirm code={promo} applied={!!(promo && promoInfo)} />

              {isSubscriber ? (
                <div className="text-center text-xs text-[var(--ink-3)]" data-testid="pricing-mastery-annual-current">
                  You're already on Mastery — manage your plan from Settings.
                </div>
              ) : !webCheckoutAllowed ? (
                <NativeWebCheckoutNotice testid="pricing-mastery-annual-native-notice" />
              ) : (
                <button
                  type="button"
                  onClick={() => startCheckout("mastery_annual")}
                  disabled={loadingPlan !== null}
                  className="btn-duo w-full justify-center"
                  data-testid="pricing-mastery-annual-cta"
                  data-track="checkout-click"
                >
                  {loadingPlan === "mastery_annual" ? <span className="spinner" /> : <>Continue Your Journey <ArrowRight size={14} /></>}
                </button>
              )}
              {error && (
                <div className="text-xs text-[var(--accent,#FF5C2A)] text-center" data-testid="pricing-error">{error}</div>
              )}
            </div>
          </div>
        </div>

        {/* Referral callout — drives viral acquisition + annual conversion */}
        <div
          className="mt-10 brut p-6 sm:p-7 max-w-4xl mx-auto"
          style={{
            background: "linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(37,99,235,0.07) 100%)",
            borderColor: "rgba(16,185,129,0.35)",
          }}
          data-testid="pricing-referral-callout"
        >
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #10B981 0%, #059669 100%)" }}
            >
              <Sparkles size={22} color="#fff" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <span className="kicker">Bring a friend</span>
              <h3 className="font-display text-2xl mt-1 leading-tight">
                Invite a friend and you both win.
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm text-[var(--ink-2)]">
                <li className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 text-[var(--emerald,#10B981)] flex-shrink-0" />
                  <span><strong className="text-[var(--ink)]">90% off</strong> AI Mastery Monthly</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 text-[var(--emerald,#10B981)] flex-shrink-0" />
                  <span><strong className="text-[var(--ink)]">30% off</strong> AI Mastery Annual</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 text-[var(--emerald,#10B981)] flex-shrink-0" />
                  <span>Annual discount must be redeemed within 30 days.</span>
                </li>
              </ul>
              <Link
                to="/refer"
                className="inline-flex items-center min-h-[44px] gap-1.5 mt-4 text-sm font-medium text-[var(--primary,#2563EB)] hover:underline"
                data-testid="pricing-view-refer"
              >
                Get your referral code <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        </div>

        {/* AI Power Boosts callout */}
        <div className="mt-12 brut p-7 max-w-4xl mx-auto" data-testid="pricing-boosts-callout">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)" }}>
              <Zap size={22} color="#fff" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <span className="kicker">AI Power Boosts</span>
              <h3 className="font-display text-2xl mt-1 leading-tight">Unlock deeper experiences any time.</h3>
              <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
                One-tap power-ups for deep challenge analysis, personalized prompt packs, and custom AI mentor feedback.
                Subscribers receive {MONTHLY_CREDITS} credits automatically every month — top up any time.
              </p>
              <Link
                to="/power-boosts"
                className="inline-flex items-center min-h-[44px] gap-1.5 mt-3 text-sm font-medium text-[var(--primary,#2563EB)] hover:underline"
                data-testid="pricing-view-boosts"
              >
                Explore AI Power Boosts <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        </div>

        {/* ── Your Personal AI Coach — live paid feature ──────────────────
         * Positioned ABOVE the Coming Soon section so it reads as a real
         * benefit you get when you upgrade, not a roadmap item. */}
        <div className="mt-12 max-w-4xl mx-auto" data-testid="pricing-coach-feature">
          <div
            className="brut p-6 sm:p-8"
            style={{
              background: "linear-gradient(135deg, rgba(37,99,235,0.07), rgba(124,58,237,0.05) 65%, #FFFFFF 100%)",
              borderColor: "rgba(37,99,235,0.4)",
            }}
          >
            <div className="grid sm:grid-cols-[auto,1fr] gap-5 items-start">
              <div className="w-14 h-14 rounded-2xl bg-[var(--primary,#2563EB)] text-white flex items-center justify-center flex-shrink-0">
                <Sparkles size={26} strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="kicker">Live now</span>
                  <span
                    className="inline-flex items-center text-[10px] font-bold tracking-[0.16em] uppercase rounded-full px-2 py-1"
                    style={{ background: "var(--emerald, #10B981)", color: "white" }}
                  >
                    Included with paid plans
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl tracking-tight leading-tight">
                  Your Personal AI Coach
                </h3>
                <p className="text-sm sm:text-base text-[var(--ink-2)] mt-2 leading-relaxed">
                  Get guided help based on your goals, lessons, and progress.
                </p>
                <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">
                  Your AI Coach helps you understand what to practice, how to
                  improve, and how to use AI for real goals like saving time,
                  making money, building things, and automating work.
                </p>
                <div className="mt-5">
                  <Link
                    to="/coach"
                    onClick={() => capture("ai_coach_clicked", { source: "pricing_section", is_paid: !!isSubscriber })}
                    className="btn-duo inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm"
                    data-testid={isSubscriber ? "pricing-coach-cta-ask" : "pricing-coach-cta-unlock"}
                  >
                    {isSubscriber ? "Ask My Coach" : "Unlock AI Coach"}
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Coming Soon: More Ways to Build Real AI Skill ──────────────
         * Outcome-focused expansions — true future features only. AI Coaches
         * is positioned ABOVE this as a live paid feature, not coming soon. */}
        <div className="mt-12 max-w-4xl mx-auto" data-testid="pricing-expansions-section">
          <div className="text-center mb-7">
            <span className="kicker">More Ways to Grow</span>
            <h3 className="font-display text-2xl sm:text-3xl mt-1.5 leading-tight">
              Coming Soon: More Ways to Build Real AI Skill
            </h3>
            <p className="text-sm text-[var(--ink-2)] mt-2 max-w-2xl mx-auto leading-relaxed">
              AI Prompt Dojo is growing into your daily AI training partner —
              helping you save time, make money, build smarter, and feel
              confident using AI.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="pricing-expansions-grid">
            {[
              {
                label: "Real-World Practice Challenges",
                desc: "Practice using AI in real situations so you can save time, make money, and solve problems faster.",
              },
              {
                label: "AI Skill Certificates",
                desc: "Earn proof of your progress as you build stronger AI skills.",
              },
              {
                label: "Advanced Growth Tracking",
                desc: "See what you're improving, what to work on next, and how far you've come.",
              },
            ].map((x) => (
              <li
                key={x.label}
                className="rounded-2xl border p-4 sm:p-5"
                style={{ borderColor: "var(--line)", background: "var(--bg-2)" }}
                data-testid={`pricing-expansion-${x.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-display text-[15px] sm:text-base leading-tight font-semibold text-[var(--ink)]">
                    {x.label}
                  </div>
                  <span className="inline-flex items-center text-[10px] font-bold tracking-[0.16em] uppercase rounded-full px-2 py-1 bg-[var(--primary-soft,#EEF2FF)] text-[var(--primary,#1E3A8A)]">
                    Coming soon
                  </span>
                </div>
                <p className="text-[13px] text-[var(--ink-2)] mt-2 leading-snug">{x.desc}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h3 className="font-display text-3xl mb-7 leading-tight">FAQ's</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { q: "What's the difference between Free and Mastery?", a: "Free is one personalized challenge per day — perfect for building the habit. Mastery unlocks unlimited daily challenges, every belt path, and 150 AI Power credits each month." },
              { q: "Can I cancel anytime?", a: "Yes. Cancel from Settings → Manage subscription. You'll keep access until the end of your billing period." },
              { q: "What are AI Power credits?", a: "Optional boosts for deeper AI experiences — deep challenge analysis, custom mentor feedback, prompt optimization, and more. Subscribers receive 150 every month, included." },
              { q: "What if I switch from monthly to annual later?", a: "From the billing portal, switch any time. You're prorated for the remaining period." },
            ].map((f) => (
              <div key={f.q} className="brut p-5">
                <div className="font-display text-base leading-tight mb-1.5">{f.q}</div>
                <div className="text-sm text-[var(--ink-2)] leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/**
 * Apple 3.1.1 compliance — when the iOS shell is running, hide Stripe
 * checkout buttons and show a static notice instead. Phase 2 will replace
 * this with a RevenueCat-backed "Subscribe" button. Per Apple's anti-
 * steering rules we deliberately don't link out to the web checkout.
 */
function NativeWebCheckoutNotice({ testid }) {
  return (
    <div
      className="text-center text-xs text-[var(--ink-3)] leading-relaxed px-2"
      data-testid={testid}
    >
      <Smartphone size={14} className="inline-block mr-1 -mt-0.5" />
      Subscriptions are managed on the web for now.
      <br />
      In-app subscribing is coming soon.
    </div>
  );
}
